import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { getOffsetDate, startOfDay } from '@app/core/util/date_utils';
import { Popover } from 'primeng/popover';
import {
  AlertBulkActionValue,
  AlertFilterValue,
  IAlertBulkActionOption,
  IAlertFilterOption,
  IAlertTestItem,
} from '@app/account/notifications/interfaces/notifications-data.interfaces';

@Component({
  selector: 'app-notifications-v2',
  templateUrl: './notifications-v2.component.html',
  standalone: false,
})
export class NotificationsV2Component {
  private _router: Router = inject(Router);

  layout = input<'menu' | 'standalone'>('standalone');
  notifications = input<IAlertTestItem[]>([]);

  notificationCountChange = output<number>();
  markAllAsRead = output<void>();
  archiveAllRead = output<void>();
  archiveAll = output<void>();

  toggleMarkAsRead = output<IAlertTestItem>();
  archiveAlert = output<IAlertTestItem>();
  notificationClick = output<IAlertTestItem>();

  notificationPopover = viewChild<Popover>('notificationPopover');
  notificationFilterPopover = viewChild<Popover>('notificationFilterPopover');
  notificationBulkActionsPopover = viewChild<Popover>(
    'notificationBulkActionsPopover',
  );

  notificationFilterPopoverVisible = signal<boolean>(false);
  notificationBulkActionsPopoverVisible = signal<boolean>(false);
  notificationPopoverVisible = signal<boolean>(false);
  drawerVisible = signal<boolean>(false);
  isLoading = signal<boolean>(false);

  notificationsCount = computed(() => this.notifications().length);
  activeNotificationsCount = computed(
    () =>
      this.notifications().filter(
        (notification: IAlertTestItem) => !notification?.read_at,
      ).length,
  );

  notificationsTitle = computed(() => {
    return this.selectedFilter()?.value === AlertFilterValue.Archived
      ? 'Archived Notifications'
      : 'Notifications';
  });

  isMenuLayout = computed(() => this.layout() === 'menu');
  isStandaloneLayout = computed(() => this.layout() === 'standalone');
  isSidepanelLayout = computed(
    () => this.isStandaloneLayout() && this.notificationsCount() >= 6,
  );

  filteredNotifications = computed(() => {
    const list = this.notifications();
    const filter: AlertFilterValue | undefined = this.selectedFilter()?.value;
    if (!filter) return list.filter((a: IAlertTestItem) => !a.archived_at);
    switch (filter) {
      case AlertFilterValue.UnreadOnly:
        return list.filter((a: IAlertTestItem) => !a.archived_at && !a.read_at);
      case AlertFilterValue.Archived:
        return list.filter((a: IAlertTestItem) => !!a.archived_at);
      default:
        return list.filter((a: IAlertTestItem) => !a.archived_at);
    }
  });

  filteredNotificationsCount = computed(
    () => this.filteredNotifications().length,
  );

  notificationsToday = computed(() => {
    const startOfToday: Date = startOfDay(new Date());
    return this.filteredNotifications().filter(
      (notification) => notification.sent_at >= startOfToday,
    );
  });

  notificationsYesterday = computed(() => {
    const startOfToday: Date = startOfDay(new Date());
    const startOfYesterday: Date = startOfDay(getOffsetDate(1));
    return this.filteredNotifications().filter(
      (notification) =>
        notification.sent_at >= startOfYesterday &&
        notification.sent_at < startOfToday,
    );
  });

  notificationsOlder = computed(() => {
    const startOfYesterday: Date = startOfDay(getOffsetDate(1));
    return this.filteredNotifications().filter(
      (notification) => notification.sent_at < startOfYesterday,
    );
  });

  showFilterIndicator = computed(() => {
    const value = this.selectedFilter()?.value;
    return (
      value === AlertFilterValue.UnreadOnly ||
      value === AlertFilterValue.Archived
    );
  });

  filterOptions: IAlertFilterOption[] = [
    {
      label: 'Unread & read',
      value: AlertFilterValue.UnreadAndRead,
      iconUrl: 'assets/icons/general/action-list.svg',
    },
    {
      label: 'Unread only',
      value: AlertFilterValue.UnreadOnly,
      iconUrl: 'assets/icons/general/communication-unread.svg',
    },
    {
      label: 'Archived',
      value: AlertFilterValue.Archived,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
  ];

  bulkActionOptions: IAlertBulkActionOption[] = [
    {
      label: 'Mark all as read',
      value: AlertBulkActionValue.MarkAllRead,
      iconUrl: 'assets/icons/general/system-feedback-success.svg',
    },
    {
      label: 'Archive all',
      value: AlertBulkActionValue.ArchiveAll,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
    {
      label: 'Archive all read',
      value: AlertBulkActionValue.ArchiveAllRead,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
  ];

  selectedFilter = signal<IAlertFilterOption | null>(this.filterOptions[0]);
  selectedBulkAction = signal<IAlertBulkActionOption | null>(null);

  constructor() {
    effect(() => {
      this.notificationCountChange.emit(this.notificationsCount());
    });

    effect(() => {
      if (!this.drawerVisible()) {
        this.selectedFilter.set(this.filterOptions[0]);
      }
    });
  }

  onStandaloneButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.isSidepanelLayout()) {
      this.drawerVisible.update((visible: boolean) => !visible);
      this.notificationPopover()?.hide();
    } else {
      this.notificationPopover()?.toggle(event);
      this.drawerVisible.set(false);
    }
  }

  onNotificationPopoverShow(): void {
    this.notificationPopoverVisible.set(true);
  }

  onNotificationPopoverHide(): void {
    this.notificationPopoverVisible.set(false);
    this.selectedFilter.set(this.filterOptions[0]);
  }

  onNotificationFilterPopoverShow(): void {
    this.notificationFilterPopoverVisible.set(true);
  }

  onNotificationFilterPopoverHide(): void {
    this.notificationFilterPopoverVisible.set(false);
  }

  onNotificationBulkActionsPopoverShow(): void {
    this.notificationBulkActionsPopoverVisible.set(true);
  }

  onNotificationBulkActionsPopoverHide(): void {
    this.notificationBulkActionsPopoverVisible.set(false);
  }

  onFilterButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.notificationBulkActionsPopoverVisible()) {
      this.notificationBulkActionsPopover()?.hide();
    }

    this.notificationFilterPopover()?.toggle(event);
  }

  onFilterSelectionChange(selectedFilter: IAlertFilterOption | null): void {
    if (!selectedFilter) return;

    this.selectedFilter.set(selectedFilter);
    this.notificationFilterPopover()?.hide();
  }

  onBulkActionsButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.notificationFilterPopoverVisible()) {
      this.notificationFilterPopover()?.hide();
    }

    this.notificationBulkActionsPopover()?.toggle(event);
  }

  onBulkActionSelectionChange(selected: IAlertBulkActionOption | null): void {
    const value: AlertBulkActionValue | undefined = selected?.value;
    if (!value) return;

    switch (value) {
      case AlertBulkActionValue.MarkAllRead:
        this.onMarkAllAsRead();
        break;
      case AlertBulkActionValue.ArchiveAllRead:
        this.onArchiveAllRead();
        break;
      case AlertBulkActionValue.ArchiveAll:
        this.onArchiveAll();
        break;
    }

    this.selectedBulkAction.set(null);
    this.notificationBulkActionsPopover()?.hide();
  }

  onMarkAllAsRead(): void {
    this.markAllAsRead.emit();
  }

  onArchiveAllRead(): void {
    this.archiveAllRead.emit();
  }

  onArchiveAll(): void {
    this.archiveAll.emit();
  }

  openSettings(): void {
    this._router.navigate([
      '/account/notification-preferences/general-preferences',
    ]);

    this._hideView();
  }

  onToggleMarkAsRead(alert: IAlertTestItem): void {
    this.toggleMarkAsRead.emit(alert);
  }

  onAlertArchive(alert: IAlertTestItem): void {
    this.archiveAlert.emit(alert);
  }

  onAlertClick(alert: IAlertTestItem): void {
    if (!alert) {
      return;
    }

    this._hideView();
    this.notificationClick.emit(alert);
  }

  private _hideView(): void {
    this.notificationPopover()?.hide();
    this.notificationFilterPopover()?.hide();
    this.notificationBulkActionsPopover()?.hide();

    this.drawerVisible.set(false);
  }
}
